<template>
  <div class="ctc-ai-wizard">

    <!-- ══════════════════════════════════════════════════════════════
         ONLY STEP: AI Closing Conditions
         wizardStep === 0 always (single step)
    ══════════════════════════════════════════════════════════════ -->
    <!-- ── Loading initial status ──────────────────────────────────── -->
    <div v-if="isLoadingStatus" class="ctc-ai__loading">
      <loader :size="'medium'" :type="'pulsing'"></loader>
      <p>Checking queue status&hellip;</p>
    </div>

    <!-- ── Main panel ───────────────────────────────────────────────── -->
    <div v-else class="ctc-ai__panel">

      <!-- AUS warning banner — hide once processing is complete -->
      <div class="ctc-ai__aus-warning" v-if="queueStatus !== 'completed'">
        <span class="k-icon k-i-warning ctc-ai__warn-icon"></span>
        <div>
          <strong>Before continuing — have you run AUS findings?</strong>
          <p>
            The AI closing conditions wizard reads your current Encompass data to populate
            condition dollar amounts. Run AUS in Encompass <em>first</em> so the findings
            reflect the final loan parameters.
          </p>
        </div>
      </div>

      <!-- ── State: NOT YET QUEUED ───────────────────────────────────── -->
      <div v-if="queueStatus === 'not_queued'" class="ctc-ai__state ctc-ai__state--idle">
        <div class="ctc-ai__idle-body">
          <span class="k-icon k-i-track-changes-enable ctc-ai__big-icon"></span>
          <h3>AI Closing Conditions</h3>
          <p>
            Click <strong>Queue for AI Processing</strong> to enqueue this loan.
            The automation will read the current Encompass field values and update
            the descriptions on the following conditions with the correct dollar amounts:
          </p>
          <ul class="ctc-ai__condition-list">
            <li><strong>CL1007</strong> — Borrower(s) cash to close cannot exceed $</li>
            <li><strong>CL1029</strong> — Monthly payment including taxes, HOA, insurance, MI not to exceed $</li>
            <li><strong>CL1032</strong> — Debts to be paid on CD</li>
            <li><strong>CL1010</strong> — EMD amount</li>
            <li><strong>CL1014</strong> — Seller Contribution limit</li>
          </ul>
          <p class="ctc-ai__note">
            <span class="k-icon k-i-information k-i-info"></span>
            CX.UWI.CTC will be set to X once conditions are updated.
          </p>
        </div>
        <k-button class="ctc-ai__enqueue-btn"
                  :look="'flat'"
                  :disabled="isEnqueueing"
                  @click="enqueue">
          <span v-if="!isEnqueueing">
            <span class="k-icon k-i-play"></span> Queue for AI Processing
          </span>
          <span v-else>
            <loader :size="'small'" :type="'pulsing'"></loader> Queuing&hellip;
          </span>
        </k-button>
      </div>

      <!-- ── State: PENDING ──────────────────────────────────────────── -->
      <div v-if="queueStatus === 'pending'" class="ctc-ai__state ctc-ai__state--pending">
        <loader :size="'large'" :type="'pulsing'"></loader>
        <h3>Processing in Queue&hellip;</h3>
        <p>
          This loan is queued for AI closing condition processing. The automation
          is reading Encompass fields and updating condition descriptions.
          This page will refresh automatically.
        </p>
        <p class="ctc-ai__meta" v-if="queueRow && queueRow.enqueuedAt">
          Enqueued {{ formatDate(queueRow.enqueuedAt) }}
          <span v-if="queueRow.enqueuedBy"> by {{ queueRow.enqueuedBy }}</span>
        </p>
        <k-button class="ctc-ai__refresh-btn" @click="pollNow" :disabled="isRefreshing">
          <span class="k-icon k-i-refresh"></span> Check Status
        </k-button>
      </div>

      <!-- ── State: COMPLETED ────────────────────────────────────────── -->
      <div v-if="queueStatus === 'completed'" class="ctc-ai__state ctc-ai__state--done">
        <span class="k-icon k-i-check-circle k-i-checkmark-circle ctc-ai__icon--success"></span>
        <h3>Conditions Updated Successfully</h3>

        <!-- AI narrative summary -->
        <div v-if="queueRow && queueRow.aiSummary" class="ctc-ai__summary">
          <p>{{ queueRow.aiSummary }}</p>
        </div>

        <!-- Condition breakdown table -->
        <div v-if="conditionResults.length > 0" class="ctc-ai__table-wrap">
          <table class="ctc-ai__table">
            <thead>
              <tr>
                <th>Condition</th>
                <th>Action</th>
                <th>Description written</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in conditionResults" :key="c.ConditionCode"
                  :class="['ctc-ai__row', `row--${c.Action?.toLowerCase()}`]">
                <td><strong>{{ c.ConditionCode }}</strong></td>
                <td>
                  <span :class="['ctc-ai__badge', `badge--${c.Action?.toLowerCase()}`]">
                    {{ c.Action }}
                  </span>
                </td>
                <td class="ctc-ai__desc">{{ truncate(c.Description, 120) }}</td>
                <td>
                  <span v-if="c.Error" class="ctc-ai__badge badge--error" :title="c.Error">Error</span>
                  <span v-else class="ctc-ai__badge badge--updated">OK</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="ctc-ai__flags">
          <span v-if="queueRow && queueRow.ctcFieldWritten"
                class="ctc-ai__flag ctc-ai__flag--ok">
            <span class="k-icon k-i-check"></span> CX.UWI.CTC set to X
          </span>
          <span v-else class="ctc-ai__flag ctc-ai__flag--warn">
            <span class="k-icon k-i-warning"></span> CX.UWI.CTC not set — check Encompass
          </span>
        </div>

        <p class="ctc-ai__meta" v-if="queueRow && queueRow.processedAt">
          Completed {{ formatDate(queueRow.processedAt) }}
        </p>

        <!-- Error details if any partial errors occurred -->
        <div v-if="queueRow && queueRow.error" class="ctc-ai__partial-error">
          <span class="k-icon k-i-warning"></span>
          <span>{{ queueRow.error }}</span>
        </div>
      </div>

      <!-- ── State: SKIPPED ──────────────────────────────────────────── -->
      <div v-if="queueStatus === 'skipped'" class="ctc-ai__state ctc-ai__state--skipped">
        <span class="k-icon k-i-warning ctc-ai__icon--warn"></span>
        <h3>Processing Skipped</h3>
        <p>{{ queueRow && queueRow.skipReason || 'This loan was skipped during processing.' }}</p>
        <k-button class="ctc-ai__enqueue-btn" @click="enqueue" :disabled="isEnqueueing">
          <span class="k-icon k-i-reload"></span> Re-queue
        </k-button>
      </div>

      <!-- ── State: ERROR ────────────────────────────────────────────── -->
      <div v-if="queueStatus === 'error'" class="ctc-ai__state ctc-ai__state--error">
        <span class="k-icon k-i-error ctc-ai__icon--error"></span>
        <h3>Processing Error</h3>
        <p class="ctc-ai__error-msg">{{ queueRow && queueRow.error }}</p>
        <p class="ctc-ai__meta" v-if="queueRow">
          Attempted {{ formatDate(queueRow.processedAt) }}
        </p>
        <k-button class="ctc-ai__enqueue-btn" @click="enqueue" :disabled="isEnqueueing">
          <span class="k-icon k-i-reload"></span> Re-queue
        </k-button>
      </div>

    </div><!-- /ctc-ai__panel -->
  </div><!-- /ctc-ai-wizard -->
</template>

<script>
  import auth from '../authConfig.js';
  import apiService from '../api/api.service.js';

  import { Loader } from "@progress/kendo-vue-indicators";
  import { Button } from '@progress/kendo-vue-buttons';

  export default {
    name: 'ClearToCloseWizard',

    // Keep same prop signature as the old wizard so dispositionMenu.vue
    // doesn't need template changes. We only actually use loanId, wizardStep,
    // and finishClicked.
    props: [
      'allDocuments', 'attachmentsSource', 'earnestMoneyDeposit',
      'fields', 'finishClicked', 'isRefi', 'loanId', 'loanType',
      'loanVOLs', 'sellerContributionFields', 'sellerContributionTotal',
      'wizardStep'
    ],

    emits: ['cleartofinish', 'documentsupdated', 'loanclearedtoclose', 'requestfailed', 'setcurrentstepcleared'],

    data() {
      return {
        queueRow: null,
        queueStatus: 'not_queued',  // not_queued | pending | completed | skipped | error
        isLoadingStatus: true,
        isEnqueueing: false,
        isRefreshing: false,
        pollTimer: null,
      };
    },

    computed: {
      conditionResults() {
        if (!this.queueRow?.resultJson) return [];
        try {
          const parsed = JSON.parse(this.queueRow.resultJson);
          // API returns PascalCase keys in resultJson
          return parsed?.Conditions ?? parsed?.conditions ?? [];
        } catch {
          return [];
        }
      }
    },

    watch: {
      // The disposition menu flips finishClicked to true when UW hits Finish.
      // We treat that as confirmation to proceed — the loan is already
      // enqueued at this point, so just emit loanclearedtoclose.
      finishClicked(val) {
        if (val) {
          this.onFinish();
        }
      },

      // Tell the parent this single step is always valid
      // (the step nav relies on setcurrentstepcleared)
      queueStatus(newStatus) {
        // Step is valid as soon as something is in the queue
        const valid = newStatus !== 'not_queued';
        this.$emit('setcurrentstepcleared', valid, 0);

        // Also enable the Finish button once completed
        if (newStatus === 'completed') {
          this.$emit('cleartofinish', true);
        }
      }
    },

    async mounted() {
      // Signal parent that this step is valid immediately
      // so the Next/Finish button logic works correctly
      this.$emit('cleartofinish', false);
      await this.loadStatus();

      // If already pending when opened, start auto-poll
      if (this.queueStatus === 'pending') {
        this.startPolling();
      }

      // If already completed, allow Finish immediately
      if (this.queueStatus === 'completed') {
        this.$emit('cleartofinish', true);
      }
    },

    beforeUnmount() {
      this.stopPolling();
    },

    methods: {
      async loadStatus() {
        this.isLoadingStatus = true;
        try {
          const tokenResponse = await auth.acquireTokenResponse();
          const res = await apiService.GetCtcConditionsStatus(this.loanId, tokenResponse.accessToken);
          if (res?.data?.data) {
            this.queueRow = this.normalizeRow(res.data.data);
            this.queueStatus = this.queueRow.status;
          }
        } catch (err) {
          console.error('[ClearToCloseWizard] Failed to load queue status:', err);
        } finally {
          this.isLoadingStatus = false;
        }
      },

      async enqueue() {
        this.isEnqueueing = true;
        try {
          const tokenResponse = await auth.acquireTokenResponse();
          const enqueuedBy = tokenResponse.idTokenClaims?.oid ?? null;
          const res = await apiService.EnqueueCtcConditions(this.loanId, enqueuedBy, tokenResponse.accessToken);
          if (res?.data?.data) {
            this.queueRow = this.normalizeRow(res.data.data);
            this.queueStatus = this.queueRow.status;
            if (this.queueStatus === 'pending') {
              this.startPolling();
            }
          }
        } catch (err) {
          console.error('[ClearToCloseWizard] Enqueue failed:', err);
          this.$emit('requestfailed');
        } finally {
          this.isEnqueueing = false;
        }
      },

      // Normalize all-lowercase API keys to the camelCase the template expects
      normalizeRow(row) {
        return {
          ...row,
          ctcFieldWritten: row.ctcfieldwritten ?? row.ctcFieldWritten ?? false,
          aiSummary: row.aisummary ?? row.aiSummary ?? '',
          resultJson: row.resultjson ?? row.resultJson ?? null,
          enqueuedAt: row.enqueuedat ?? row.enqueuedAt ?? null,
          enqueuedBy: row.enqueuedby ?? row.enqueuedBy ?? null,
          processedAt: row.processedat ?? row.processedAt ?? null,
          conditionsAdded: row.conditionsadded ?? row.conditionsAdded ?? 0,
        };
      },

      async pollNow() {
        this.isRefreshing = true;
        await this.loadStatus();
        this.isRefreshing = false;
        if (this.queueStatus !== 'pending') {
          this.stopPolling();
        }
      },

      startPolling() {
        this.stopPolling();
        this.pollTimer = setInterval(async () => {
          await this.loadStatus();
          if (this.queueStatus !== 'pending') {
            this.stopPolling();
          }
        }, 10000);
      },

      stopPolling() {
        if (this.pollTimer) {
          clearInterval(this.pollTimer);
          this.pollTimer = null;
        }
      },

      onFinish() {
        // Emit the event the parent (dispositionMenu) listens for
        // to close the wizard and mark the disposition complete
        this.$emit('loanclearedtoclose', {
          loanId: this.loanId,
          ctcFieldWritten: this.queueRow?.ctcFieldWritten ?? false,
          conditionsAdded: this.queueRow?.conditionsAdded ?? 0,
          aiSummary: this.queueRow?.aiSummary ?? ''
        });
      },

      formatDate(val) {
        if (!val) return '';
        // Server timestamps are UTC — force display in Eastern Time (ET)
        const date = new Date(val.endsWith('Z') ? val : val + 'Z');
        return date.toLocaleString('en-US', {
          timeZone: 'America/New_York',
          month: 'short', day: 'numeric',
          hour: 'numeric', minute: '2-digit',
          timeZoneName: 'short'
        });
      },

      truncate(str, max) {
        if (!str) return '';
        return str.length > max ? str.slice(0, max) + '…' : str;
      }
    },
    components: {
      'loader': Loader,
      'k-button': Button,
    }
  };
</script>

<style scoped>
  .ctc-ai-wizard {
    padding: 20px 24px;
    min-height: 320px;
  }

  /* ── Loading ─────────────────────────────────────────────────────── */
  .ctc-ai__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    min-height: 300px;
    color: #718096;
    font-size: 13px;
  }

  /* ── AUS warning banner ─────────────────────────────────────────── */
  .ctc-ai__aus-warning {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    background: #fffbeb;
    border: 1px solid #f6d860;
    border-left: 4px solid #d69e2e;
    border-radius: 6px;
    padding: 14px 16px;
    margin-bottom: 24px;
  }

  .ctc-ai__warn-icon {
    font-size: 22px;
    color: #d69e2e;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .ctc-ai__aus-warning strong {
    display: block;
    font-size: 14px;
    color: #744210;
    margin-bottom: 4px;
  }

  .ctc-ai__aus-warning p {
    margin: 0;
    font-size: 13px;
    color: #744210;
    line-height: 1.5;
  }

  /* ── State containers ────────────────────────────────────────────── */
  .ctc-ai__state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    text-align: center;
    padding: 16px 12px;
  }

    .ctc-ai__state h3 {
      font-size: 17px;
      font-weight: 600;
      margin: 0;
      color: #0b1f3a;
    }

    .ctc-ai__state p {
      font-size: 13px;
      color: #4a5568;
      max-width: 520px;
      margin: 0;
      line-height: 1.55;
    }

  /* ── Idle state ──────────────────────────────────────────────────── */
  .ctc-ai__idle-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    max-width: 540px;
  }

  .ctc-ai__big-icon {
    font-size: 42px;
    color: #0b1f3a;
    margin-bottom: 4px;
  }

  .ctc-ai__condition-list {
    text-align: left;
    font-size: 13px;
    color: #2d3748;
    margin: 4px 0 0 0;
    padding-left: 20px;
    line-height: 1.9;
  }

  .ctc-ai__note {
    font-size: 12px;
    color: #718096;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  /* ── Icons ───────────────────────────────────────────────────────── */
  .ctc-ai__state > .k-icon {
    font-size: 40px;
    margin-bottom: 4px;
  }

  .ctc-ai__icon--success {
    color: #38a169;
  }

  .ctc-ai__icon--warn {
    color: #d69e2e;
  }

  .ctc-ai__icon--error {
    color: #c53030;
  }

  /* ── Enqueue / Re-queue button ───────────────────────────────────── */
  .ctc-ai__enqueue-btn {
    background: #0b1f3a !important;
    color: #fff !important;
    border: none !important;
    padding: 9px 22px !important;
    border-radius: 4px !important;
    font-size: 13px !important;
    font-weight: 600 !important;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 6px;
  }

    .ctc-ai__enqueue-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

  .ctc-ai__refresh-btn {
    font-size: 12px;
  }

  /* ── AI summary block ────────────────────────────────────────────── */
  .ctc-ai__summary {
    background: #f7fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 12px 16px;
    font-size: 13px;
    color: #2d3748;
    text-align: left;
    max-width: 580px;
    width: 100%;
    line-height: 1.6;
  }

  /* ── Condition table ─────────────────────────────────────────────── */
  .ctc-ai__table-wrap {
    width: 100%;
    max-width: 620px;
    overflow-x: auto;
    text-align: left;
  }

  .ctc-ai__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
  }

    .ctc-ai__table th {
      background: #0b1f3a;
      color: #fff;
      padding: 7px 11px;
      text-align: left;
      font-weight: 500;
      white-space: nowrap;
    }

    .ctc-ai__table td {
      padding: 6px 11px;
      border-bottom: 1px solid #e2e8f0;
      vertical-align: top;
    }

  .ctc-ai__desc {
    color: #4a5568;
    max-width: 300px;
  }

  /* ── Badges ──────────────────────────────────────────────────────── */
  .ctc-ai__badge {
    display: inline-block;
    padding: 2px 9px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
    text-transform: capitalize;
    white-space: nowrap;
  }

  .badge--updated {
    background: #c6f6d5;
    color: #276749;
  }

  .badge--added {
    background: #bee3f8;
    color: #2a6496;
  }

  .badge--not_found {
    background: #fed7d7;
    color: #9b2c2c;
  }

  .badge--error {
    background: #fed7d7;
    color: #9b2c2c;
  }

  .badge--dry_run {
    background: #e9d8fd;
    color: #553c9a;
  }

  /* ── Flags row ───────────────────────────────────────────────────── */
  .ctc-ai__flags {
    display: flex;
    gap: 14px;
    flex-wrap: wrap;
    justify-content: center;
  }

  .ctc-ai__flag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 500;
  }

  .ctc-ai__flag--ok {
    color: #276749;
  }

  .ctc-ai__flag--warn {
    color: #744210;
  }

  /* ── Meta / timestamps ───────────────────────────────────────────── */
  .ctc-ai__meta {
    font-size: 11px;
    color: #718096;
  }

  /* ── Error message ───────────────────────────────────────────────── */
  .ctc-ai__error-msg {
    font-size: 13px;
    color: #9b2c2c;
    background: #fff5f5;
    border: 1px solid #fed7d7;
    border-radius: 4px;
    padding: 10px 14px;
    max-width: 500px;
    text-align: left;
    line-height: 1.5;
  }

  .ctc-ai__partial-error {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-size: 12px;
    color: #744210;
    background: #fffbeb;
    border: 1px solid #f6d860;
    border-radius: 4px;
    padding: 8px 12px;
    max-width: 500px;
    text-align: left;
  }
</style>
